import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export default function KPIs() {
  const { user, logout, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/kpis`)
      .then(r => setData(r.data))
      .catch(() => setError('No se pudieron cargar los indicadores'))
      .finally(() => setLoading(false));
  }, [API]);

  const fmt = n => Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 });
  const fmtPct = n => `${Number(n || 0).toFixed(1)}%`;
  const fmtCop = n => `$${Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 })}`;

  const colorPct = pct => pct >= 95 ? '#16a34a' : pct >= 80 ? '#f59e0b' : '#dc2626';
  const bgPct    = pct => pct >= 95 ? '#f0fdf4' : pct >= 80 ? '#fffbeb' : '#fff5f5';

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.navBrand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <span style={s.navUser}>👤 {user?.nombre}</span>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <a href="/" style={s.navLink}>⚡ Conciliar</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <a href="/calendario-tributario" style={s.navLink}>📅 Calendario</a>
          <a href="/retencion-fuente" style={s.navLink}>🧾 Retención</a>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      <div style={s.content}>
        <h1 style={s.title}>📊 Panel de indicadores</h1>

        {loading && <div style={s.empty}>Cargando indicadores...</div>}
        {error   && <div style={s.errorBox}>{error}</div>}

        {data && (
          <>
            {/* ── Cards de resumen ── */}
            <div style={s.cards}>
              <Card titulo="Total conciliaciones" valor={fmt(data.total)} icono="📁" color="#2563eb" />
              <Card titulo="% promedio conciliado" valor={fmtPct(data.pct_promedio)}
                icono={data.pct_promedio >= 95 ? '✅' : data.pct_promedio >= 80 ? '⚠️' : '🔴'}
                color={colorPct(data.pct_promedio)} bg={bgPct(data.pct_promedio)} />
              <Card titulo="Partidas pendientes" valor={fmt(data.partidas_pendientes)} icono="⏳" color="#f59e0b" />
              <Card titulo="Valor sin resolver" valor={fmtCop(data.partidas_valor)} icono="💰" color="#dc2626" />
            </div>

            <div style={s.grid2}>
              {/* ── Tendencia 6 meses ── */}
              <div style={s.panel}>
                <h2 style={s.panelTitle}>Tendencia últimos 6 meses</h2>
                {data.tendencia.length === 0
                  ? <p style={s.empty2}>Sin datos en este período</p>
                  : data.tendencia.map(t => (
                    <div key={t.mes} style={s.barRow}>
                      <span style={s.barLabel}>{t.mes}</span>
                      <div style={s.barTrack}>
                        <div style={{
                          ...s.barFill,
                          width: `${t.pct || 0}%`,
                          background: colorPct(t.pct),
                        }} />
                      </div>
                      <span style={{ ...s.barPct, color: colorPct(t.pct) }}>
                        {fmtPct(t.pct)}
                      </span>
                      <span style={s.barCount}>{t.total} conc.</span>
                    </div>
                  ))
                }
              </div>

              {/* ── Por banco ── */}
              <div style={s.panel}>
                <h2 style={s.panelTitle}>Por banco</h2>
                {data.por_banco.length === 0
                  ? <p style={s.empty2}>Sin datos</p>
                  : data.por_banco.map(b => (
                    <div key={b.banco} style={s.bancoRow}>
                      <div style={s.bancoInfo}>
                        <span style={s.bancoNombre}>{b.banco || '—'}</span>
                        <span style={s.bancoTotal}>{b.total} conciliaciones</span>
                      </div>
                      <div style={s.barTrack}>
                        <div style={{
                          ...s.barFill,
                          width: `${b.pct || 0}%`,
                          background: colorPct(b.pct),
                        }} />
                      </div>
                      <span style={{ ...s.barPct, color: colorPct(b.pct) }}>
                        {fmtPct(b.pct)}
                      </span>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* ── Recientes ── */}
            {data.recientes.length > 0 && (
              <div style={s.panel}>
                <h2 style={s.panelTitle}>Últimas 5 conciliaciones</h2>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {['Nombre', 'Banco', 'Mes', '% Conciliado', 'Fecha'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.recientes.map((r, i) => (
                      <tr key={i}>
                        <td style={s.td}>{r.nombre}</td>
                        <td style={s.td}>{r.banco}</td>
                        <td style={s.td}>{r.mes}</td>
                        <td style={s.td}>
                          <span style={{
                            ...s.badge,
                            background: bgPct(r.pct),
                            color: colorPct(r.pct),
                          }}>
                            {fmtPct(r.pct)}
                          </span>
                        </td>
                        <td style={s.td}>{(r.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Card({ titulo, valor, icono, color, bg }) {
  return (
    <div style={{ ...s.card, background: bg || '#fff' }}>
      <div style={s.cardIcon}>{icono}</div>
      <div style={{ ...s.cardVal, color: color || '#111' }}>{valor}</div>
      <div style={s.cardTit}>{titulo}</div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
    background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '28px 24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 22, fontWeight: 700, margin: '0 0 24px', color: '#111' },
  errorBox: { color: '#dc2626', background: '#fff5f5', padding: 12, borderRadius: 8,
    border: '1px solid #fee2e2', marginBottom: 16 },
  empty: { textAlign: 'center', color: '#888', padding: 40 },
  empty2: { color: '#aaa', fontSize: 13, margin: '12px 0' },

  cards: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 },
  card: { background: '#fff', borderRadius: 14, padding: '20px', border: '1px solid #e5e7eb',
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
  cardIcon: { fontSize: 22, marginBottom: 4 },
  cardVal: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
  cardTit: { fontSize: 12, color: '#666', marginTop: 4 },

  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  panel: { background: '#fff', borderRadius: 14, padding: '20px', border: '1px solid #e5e7eb', marginBottom: 16 },
  panelTitle: { fontSize: 15, fontWeight: 600, margin: '0 0 16px', color: '#111' },

  barRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  barLabel: { fontSize: 12, color: '#555', width: 60, flexShrink: 0 },
  barTrack: { flex: 1, height: 10, background: '#f0f0f0', borderRadius: 6, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 6, transition: 'width 0.6s ease' },
  barPct: { fontSize: 12, fontWeight: 600, width: 48, textAlign: 'right', flexShrink: 0 },
  barCount: { fontSize: 11, color: '#aaa', width: 60, flexShrink: 0 },

  bancoRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  bancoInfo: { display: 'flex', flexDirection: 'column', width: 160, flexShrink: 0 },
  bancoNombre: { fontSize: 13, fontWeight: 500, color: '#333' },
  bancoTotal: { fontSize: 11, color: '#aaa' },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 500,
    color: '#555', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f0f0f0' },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
};
